import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { formatAddressLine, geocodeAddress } from '../lib/geocode';
import { lookupIpLocation } from '../lib/ipGeolocation';
import { useGoogleMaps } from '../components/providers/GoogleMapsProvider';

export type LocationSource = 'profile' | 'manual' | 'device' | 'ip';

export interface ShopperLocation {
  street?: string;
  city: string;
  state: string;
  zipCode: string;
  lat: number | null;
  lng: number | null;
  source: LocationSource;
  /** Set when the location came from a saved address in the user's profile. */
  addressId?: string;
}

export interface ShopperLocationInput {
  street?: string;
  city: string;
  state: string;
  zipCode: string;
  lat?: number | null;
  lng?: number | null;
  addressId?: string;
}

interface ShopperLocationContextValue {
  location: ShopperLocation | null;
  loading: boolean;
  /** Short display string for the location pin, e.g. "Soho, NY". */
  label: string;
  /** True once we have coordinates good enough to filter by distance. */
  hasCoords: boolean;
  setLocation: (input: ShopperLocationInput, source: LocationSource) => Promise<void>;
  /** Drop any manual override and fall back to profile/IP resolution. */
  resetLocation: () => void;
}

const MANUAL_STORAGE_KEY = 'fash_shopper_location';

const ShopperLocationContext = createContext<ShopperLocationContextValue | null>(null);

function readStoredLocation(): ShopperLocation | null {
  try {
    const raw = localStorage.getItem(MANUAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ShopperLocation;
    if (!parsed?.city && !parsed?.zipCode) return null;
    return parsed;
  } catch {
    return null;
  }
}

function storeLocation(loc: ShopperLocation | null) {
  try {
    if (loc) localStorage.setItem(MANUAL_STORAGE_KEY, JSON.stringify(loc));
    else localStorage.removeItem(MANUAL_STORAGE_KEY);
  } catch {
    // Storage unavailable - the location simply won't survive a reload.
  }
}

export function formatLocationLabel(loc: ShopperLocation | null): string {
  if (!loc) return 'Set location';
  if (loc.city && loc.state) return `${loc.city}, ${loc.state}`;
  if (loc.city) return loc.city;
  if (loc.zipCode) return loc.zipCode;
  return 'Set location';
}

export function ShopperLocationProvider({ children }: { children: ReactNode }) {
  const { isLoaded: mapsLoaded } = useGoogleMaps();
  const [location, setLocationState] = useState<ShopperLocation | null>(readStoredLocation);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);

  // A location the user picked themselves always outranks anything we infer.
  const hasManualOverride = useRef(
    location?.source === 'manual' || location?.source === 'device'
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUserId(u?.uid ?? null);
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const geocodeIfNeeded = useCallback(
    async (addr: { street?: string; city: string; state: string; zipCode: string }) => {
      if (!mapsLoaded) return null;
      return geocodeAddress(formatAddressLine(addr));
    },
    [mapsLoaded]
  );

  const resolveFromProfile = useCallback(
    async (uid: string): Promise<ShopperLocation | null> => {
      const snap = await getDocs(
        query(collection(db, 'users', uid, 'addresses'), orderBy('isDefault', 'desc'))
      );
      if (snap.empty) return null;

      const first = snap.docs[0];
      const data = first.data() as {
        street?: string;
        city?: string;
        state?: string;
        zip?: string;
        lat?: number;
        lng?: number;
      };

      const resolved: ShopperLocation = {
        street: data.street || '',
        city: data.city || '',
        state: data.state || '',
        zipCode: data.zip || '',
        lat: typeof data.lat === 'number' ? data.lat : null,
        lng: typeof data.lng === 'number' ? data.lng : null,
        source: 'profile',
        addressId: first.id,
      };

      // Saved addresses are stored as plain text, so fill in coordinates on the
      // fly to make distance filtering possible.
      if (resolved.lat === null) {
        const point = await geocodeIfNeeded(resolved);
        if (point) {
          resolved.lat = point.lat;
          resolved.lng = point.lng;
        }
      }

      return resolved;
    },
    [geocodeIfNeeded]
  );

  /** Profile address when signed in, otherwise an IP-derived approximation. */
  const resolveAutomatic = useCallback(
    async (uid: string | null): Promise<ShopperLocation> => {
      if (uid) {
        try {
          const fromProfile = await resolveFromProfile(uid);
          if (fromProfile) return fromProfile;
        } catch (err) {
          console.error('[ShopperLocation] Failed to read profile address:', err);
        }
      }

      const ip = await lookupIpLocation();
      return {
        city: ip.city,
        state: ip.state,
        zipCode: ip.zipCode,
        lat: ip.lat,
        lng: ip.lng,
        source: 'ip',
      };
    },
    [resolveFromProfile]
  );

  useEffect(() => {
    if (!authReady) return;
    if (hasManualOverride.current) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    resolveAutomatic(userId).then((resolved) => {
      if (cancelled) return;
      setLocationState(resolved);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [authReady, userId, resolveAutomatic]);

  const setLocation = useCallback(
    async (input: ShopperLocationInput, source: LocationSource) => {
      let lat = typeof input.lat === 'number' ? input.lat : null;
      let lng = typeof input.lng === 'number' ? input.lng : null;

      if (lat === null) {
        const point = await geocodeIfNeeded({
          street: input.street,
          city: input.city,
          state: input.state,
          zipCode: input.zipCode,
        });
        if (point) {
          lat = point.lat;
          lng = point.lng;
        }
      }

      const next: ShopperLocation = {
        street: input.street || '',
        city: input.city,
        state: input.state,
        zipCode: input.zipCode,
        lat,
        lng,
        source,
        addressId: input.addressId,
      };

      hasManualOverride.current = true;
      setLocationState(next);
      storeLocation(next);
    },
    [geocodeIfNeeded]
  );

  const resetLocation = useCallback(() => {
    hasManualOverride.current = false;
    storeLocation(null);
    setLoading(true);
    resolveAutomatic(userId).then((resolved) => {
      setLocationState(resolved);
      setLoading(false);
    });
  }, [userId, resolveAutomatic]);

  const value = useMemo<ShopperLocationContextValue>(
    () => ({
      location,
      loading,
      label: formatLocationLabel(location),
      hasCoords: typeof location?.lat === 'number' && typeof location?.lng === 'number',
      setLocation,
      resetLocation,
    }),
    [location, loading, setLocation, resetLocation]
  );

  return (
    <ShopperLocationContext.Provider value={value}>{children}</ShopperLocationContext.Provider>
  );
}

export function useShopperLocation(): ShopperLocationContextValue {
  const ctx = useContext(ShopperLocationContext);
  if (!ctx) {
    throw new Error('useShopperLocation must be used within a ShopperLocationProvider');
  }
  return ctx;
}
